import { useState, useReducer, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation } from 'convex/react';
import type { Id } from '../../convex/_generated/dataModel';
import { api } from '../../convex/_generated/api';
import { usePropertyScrapingWorkflow } from '@/hooks/usePropertyScraping';
import { usePropertyVideos, usePropertyScenes } from '@/hooks/usePropertyVideoGeneration';
import { useServerFullVideoGenerator } from '@/hooks/useServerFullVideoGenerator';
import { usePropertyVideoAdapter } from '@/adapters/propertyVideoAdapter';
import type { IFullVideoGeneratorAdapter } from '@/@types/fullVideoGenerator';
import {
  DEFAULT_VOICE_ID,
  DEFAULT_DURATION,
} from '@/components/property/PropertyVideoWizardComponents';
import type { PropertyVideoWizardStep, PropertyWizardFlow } from '@/@types/property';

type WizardState = {
  currentStep: PropertyVideoWizardStep;
  propertyId: Id<'propertyListings'> | undefined;
  currentVideoId: Id<'propertyVideos'> | null;
  hasInitialized: boolean;
};

type WizardAction =
  | { type: 'SET_STEP'; payload: PropertyVideoWizardStep }
  | {
      type: 'INIT';
      payload: { step: PropertyVideoWizardStep; videoId: Id<'propertyVideos'> | null };
    }
  | { type: 'INIT_DEFAULT' }
  | {
      type: 'URL_SUBMITTED';
      payload: { propertyId: Id<'propertyListings'>; step: PropertyVideoWizardStep };
    }
  | {
      type: 'VIDEO_CREATED';
      payload: { videoId: Id<'propertyVideos'>; step: PropertyVideoWizardStep };
    }
  | { type: 'RETRY' };

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'INIT':
      return {
        ...state,
        currentStep: action.payload.step,
        currentVideoId: action.payload.videoId,
        hasInitialized: true,
      };
    case 'INIT_DEFAULT':
      return { ...state, hasInitialized: true };
    case 'URL_SUBMITTED':
      return { ...state, propertyId: action.payload.propertyId, currentStep: action.payload.step };
    case 'VIDEO_CREATED':
      return { ...state, currentVideoId: action.payload.videoId, currentStep: action.payload.step };
    case 'RETRY':
      return { ...state, currentStep: 1 as PropertyVideoWizardStep, propertyId: undefined };
  }
}

const dummyAdapter: IFullVideoGeneratorAdapter = {
  videoType: 'property' as const,
  getVideosForConcatenation: async () => [],
  generateUploadUrl: async () => '',
  createFinalVideoRecord: async () => '',
};

export function usePropertyVideoWizard(initialPropertyId: Id<'propertyListings'> | null) {
  const [wizardState, dispatchWizard] = useReducer(wizardReducer, {
    currentStep: 1 as PropertyVideoWizardStep,
    propertyId: initialPropertyId ?? undefined,
    currentVideoId: null,
    hasInitialized: false,
  });

  const { currentStep, propertyId, currentVideoId, hasInitialized } = wizardState;

  const [selectedVoiceId, setSelectedVoiceId] = useState(DEFAULT_VOICE_ID);
  const [selectedDuration, setSelectedDuration] = useState(DEFAULT_DURATION);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<'16:9' | '9:16'>('9:16');
  const [startError, setStartError] = useState<string | null>(null);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);

  const createPendingVideo = useMutation(api.property.mutations.createPendingVideo);
  const existingProperty = useQuery(
    api.property.queries.getPropertyListing,
    initialPropertyId ? { propertyId: initialPropertyId } : 'skip'
  );
  const { latestVideo, isLoading: isVideosLoading } = usePropertyVideos(
    initialPropertyId ?? undefined
  );
  const { scenes: latestVideoScenes, isLoading: isScenesLoading } = usePropertyScenes(
    latestVideo?._id
  );

  const scrapingWorkflow = usePropertyScrapingWorkflow(propertyId);
  const property = scrapingWorkflow.property ?? existingProperty;

  // startScraping is a plain async function (not useCallback-wrapped) inside usePropertyScrapingWorkflow,
  // so its reference changes every render. Store it in a ref so handleUrlSubmit has a stable dep.
  const startScrapingRef = useRef(scrapingWorkflow.startScraping);
  useEffect(() => {
    startScrapingRef.current = scrapingWorkflow.startScraping;
  });

  const propertyVideo = useQuery(
    api.property.queries.getPropertyVideo,
    currentVideoId ? { videoId: currentVideoId } : 'skip'
  );
  const finalVideoUrl = useQuery(
    api.storage.getUrl,
    propertyVideo?.finalVideoId ? { storageId: propertyVideo.finalVideoId } : 'skip'
  );
  const { adapter: propertyVideoAdapter } = usePropertyVideoAdapter(currentVideoId);

  const {
    state: fullVideoGeneratorState,
    generateFullVideo,
    reset: resetFullVideoGenerator,
  } = useServerFullVideoGenerator(propertyVideoAdapter ?? dummyAdapter);

  const wizardFlow: PropertyWizardFlow =
    initialPropertyId && existingProperty?.status === 'ready' ? 'draft' : 'new';

  const getDisplayStep = useCallback(
    (internalStep: number): PropertyVideoWizardStep => {
      if (wizardFlow === 'new') return internalStep as PropertyVideoWizardStep;
      const mapping: Record<number, PropertyVideoWizardStep> = { 2: 1, 4: 2, 5: 3, 6: 4 };
      return mapping[internalStep] || 1;
    },
    [wizardFlow]
  );

  const getInternalStep = useCallback(
    (displayStep: number): PropertyVideoWizardStep => {
      if (wizardFlow === 'new') return displayStep as PropertyVideoWizardStep;
      const reverseMapping: Record<number, PropertyVideoWizardStep> = { 1: 2, 2: 4, 3: 5, 4: 6 };
      return reverseMapping[displayStep] || 2;
    },
    [wizardFlow]
  );

  const calculateInitialStep = useCallback((): PropertyVideoWizardStep => {
    if (!existingProperty) return 1;
    if (existingProperty.status !== 'ready') return existingProperty.imageIds.length > 0 ? 2 : 1;
    if (latestVideo) {
      if (
        ['generating_videos', 'muxing', 'ready_for_muxing', 'completed'].includes(
          latestVideo.status
        )
      )
        return 6;
      if (['pending', 'generating_scenes', 'generating_voiceovers'].includes(latestVideo.status))
        return 5;
      if (latestVideoScenes && latestVideoScenes.length > 0) return 5;
    }
    return 4;
  }, [existingProperty, latestVideo, latestVideoScenes]);

  useEffect(() => {
    if (hasInitialized) return;
    if (!initialPropertyId) {
      dispatchWizard({ type: 'INIT_DEFAULT' });
      return;
    }
    if (existingProperty === undefined) return;
    if (existingProperty?.status === 'ready') {
      if (isVideosLoading) return;
      if (latestVideo && isScenesLoading) return;
    }
    if (existingProperty) {
      dispatchWizard({
        type: 'INIT',
        payload: { step: calculateInitialStep(), videoId: latestVideo?._id ?? null },
      });
    } else {
      dispatchWizard({ type: 'INIT_DEFAULT' });
    }
  }, [
    initialPropertyId,
    existingProperty,
    hasInitialized,
    calculateInitialStep,
    latestVideo,
    isVideosLoading,
    isScenesLoading,
  ]);

  const handleUrlSubmit = useCallback(
    async (url: string) => {
      try {
        setStartError(null);
        // Call via ref so this callback doesn't re-create on every render due to
        // the unstable startScraping reference from usePropertyScrapingWorkflow.
        const result = await startScrapingRef.current(url);
        dispatchWizard({
          type: 'URL_SUBMITTED',
          payload: { propertyId: result.propertyId, step: 2 as PropertyVideoWizardStep },
        });
      } catch (error) {
        setStartError(error instanceof Error ? error.message : 'Failed to start scraping');
      }
    },
    [] // startScrapingRef is a ref — stable, no dep needed
  );

  const handleDataReviewContinue = useCallback(() => {
    dispatchWizard({
      type: 'SET_STEP',
      payload: (wizardFlow === 'draft' ? 4 : 3) as PropertyVideoWizardStep,
    });
  }, [wizardFlow]);

  const handleNearbyPlacesContinue = useCallback(() => {
    dispatchWizard({ type: 'SET_STEP', payload: 4 as PropertyVideoWizardStep });
  }, []);

  const handleImageProcessingContinue = useCallback(
    async (aspectRatio: '16:9' | '9:16') => {
      if (!propertyId) return;
      setSelectedAspectRatio(aspectRatio);
      if (currentVideoId) {
        dispatchWizard({ type: 'SET_STEP', payload: 5 as PropertyVideoWizardStep });
        return;
      }
      if (latestVideo && latestVideoScenes && latestVideoScenes.length > 0) {
        dispatchWizard({
          type: 'VIDEO_CREATED',
          payload: { videoId: latestVideo._id, step: 5 as PropertyVideoWizardStep },
        });
        return;
      }
      try {
        setIsCreatingVideo(true);
        setStartError(null);
        const videoId = await createPendingVideo({
          propertyId,
          targetDuration: selectedDuration,
          voiceId: selectedVoiceId,
          aspectRatio,
        });
        dispatchWizard({
          type: 'VIDEO_CREATED',
          payload: { videoId, step: 5 as PropertyVideoWizardStep },
        });
      } catch (error) {
        setStartError(error instanceof Error ? error.message : 'Failed to create video');
      } finally {
        setIsCreatingVideo(false);
      }
    },
    [
      propertyId,
      currentVideoId,
      latestVideo,
      latestVideoScenes,
      selectedDuration,
      selectedVoiceId,
      createPendingVideo,
    ]
  );

  const handleGoBack = useCallback(() => {
    if (currentStep > 1)
      dispatchWizard({ type: 'SET_STEP', payload: (currentStep - 1) as PropertyVideoWizardStep });
  }, [currentStep]);

  const handleRetry = useCallback(() => {
    setStartError(null);
    resetFullVideoGenerator();
    dispatchWizard({ type: 'RETRY' });
  }, [resetFullVideoGenerator]);

  const handleGenerateFullVideo = useCallback(async () => {
    dispatchWizard({ type: 'SET_STEP', payload: 6 as PropertyVideoWizardStep });
    await generateFullVideo();
  }, [generateFullVideo]);

  const handleStepClick = useCallback(
    (step: PropertyVideoWizardStep) => {
      if (step < currentStep) {
        dispatchWizard({ type: 'SET_STEP', payload: step });
        return;
      }
      if (step === 6 && propertyVideo?.finalVideoId)
        dispatchWizard({ type: 'SET_STEP', payload: step });
    },
    [currentStep, propertyVideo?.finalVideoId]
  );

  const dispatchSetStep = useCallback((step: PropertyVideoWizardStep) => {
    dispatchWizard({ type: 'SET_STEP', payload: step });
  }, []);

  return {
    currentStep,
    propertyId,
    currentVideoId,
    wizardFlow,
    selectedVoiceId,
    selectedDuration,
    selectedAspectRatio,
    startError,
    isCreatingVideo,
    property,
    propertyVideo,
    finalVideoUrl,
    fullVideoGeneratorState,
    resetFullVideoGenerator,
    existingProperty,
    isScraping: scrapingWorkflow.isScraping,
    isAnalyzing: scrapingWorkflow.isAnalyzing,
    isGenerating: scrapingWorkflow.isGenerating,
    isReady: scrapingWorkflow.isReady,
    isScrapingFailed: scrapingWorkflow.isFailed,
    scrapingStatus: scrapingWorkflow.status,
    scrapingErrorMessage: scrapingWorkflow.errorMessage,
    isScrapingInProgress: scrapingWorkflow.isScrapingInProgress,
    getDisplayStep,
    getInternalStep,
    handleUrlSubmit,
    handleDataReviewContinue,
    handleNearbyPlacesContinue,
    handleImageProcessingContinue,
    handleGoBack,
    handleRetry,
    handleGenerateFullVideo,
    handleStepClick,
    dispatchSetStep,
    setSelectedVoiceId,
    setSelectedDuration,
    setSelectedAspectRatio,
    setStartError,
  };
}
